import moment from 'moment';
import React, { Dispatch } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@xsolla/ui-core';
import { ArrowClockwise, BlockCircle, Trash } from '@xsolla/ui-icons';
import { useModal } from '@xsolla/ui-modal';

import { resetPassword, unbanUser } from '../../../../api';
import { UserSocialProfile } from '../../../../api/users/types';
import { useNotifications } from '../../../../hooks/useNotifications';
import { UserStorageTypes } from '../../DataStorageForm/settings';
import { BanUserModal } from '../BanUserModal';
import ChangeServerCustomId from '../ChangeServerCustomId';
import { SettingsBlock } from '../SettingsBlock/SettingsBlock';
import { UserFieldWrapper } from '../style';
import { Action, ActionType, UserState } from '../types';
import { SocialProfileTable } from './socialProfileTable';
import {
  ChangeServerCustomIdWrapper,
  NicknameWrapper,
  UserInformation,
  UserNameTitle
} from './style';
import UserLoginHistory from './UserLoginHistory';

type Props = {
  state: UserState;
  projectId: string;
  projectStorage: string;
  dispatch: Dispatch<Action>;
  userSocialProfile: UserSocialProfile[] | [];
  onSocialProfileRemoved: (providerName: string) => void;
};

const GeneralInfo = ({
  state,
  projectId,
  projectStorage,
  dispatch,
  userSocialProfile,
  onSocialProfileRemoved
}: Props): JSX.Element => {
  const intl = useIntl();
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const {
    id = '',
    external_id = '',
    registered = '',
    username,
    nickname = '',
    ban: banData = {},
    login_type = ''
  } = state.user;

  const [onOpenBanUserModal, onCloseBanUserModal] = useModal(
    () => (
      <BanUserModal
        onBan={(banDataResponse) => {
          dispatch({ type: ActionType.banUserEnd, payload: banDataResponse });
          onCloseBanUserModal();
        }}
        externalId={external_id}
        banData={banData}
        registered={registered}
        projectId={projectId}
        userId={id}
        onClose={onCloseBanUserModal}
      />
    ),
    []
  );

  const resetPasswordHandle = async () => {
    try {
      dispatch({ type: ActionType.resetPasswordStart });
      await resetPassword(projectId, id);
      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
    } finally {
      dispatch({ type: ActionType.resetPasswordEnd });
    }
  };

  const unbanHandle = async () => {
    try {
      dispatch({ type: ActionType.unbanUserStart });
      await unbanUser(projectId, id);
      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
    }

    dispatch({ type: ActionType.unbanUserEnd });
  };

  return (
    <SettingsBlock label={intl.messages['users.settings.general-info.label']} withBorder>
      {username && <UserNameTitle>{username}</UserNameTitle>}
      <UserInformation data-test-id="user-ID">
        <p>ID {id}</p>

        {external_id && (
          <p>
            {intl.messages['users.settings.general-info.external']} {external_id}
          </p>
        )}
        {nickname && (
          <NicknameWrapper>
            {intl.formatMessage({ id: 'users.settings.general-info.nickname' }, { nickname })}
          </NicknameWrapper>
        )}
        <p>
          {intl.messages['users.settings.general-info.registred']}{' '}
          {moment(registered).format('L LT')}
        </p>
        {login_type && (
          <p>
            {intl.messages['users.settings.general-info.login-type']}:{' '}
            {intl.messages[`users.settings.general-info.login-type.${login_type}`]
              ? intl.messages[`users.settings.general-info.login-type.${login_type}`]
              : login_type}
          </p>
        )}
        {userSocialProfile.length > 0 && (
          <SocialProfileTable
            projectId={projectId}
            userId={id}
            userSocialProfile={userSocialProfile}
            onSocialProfileRemoved={onSocialProfileRemoved}
          />
        )}
        <ChangeServerCustomIdWrapper>
          <ChangeServerCustomId state={state} projectId={projectId} dispatch={dispatch} />
        </ChangeServerCustomIdWrapper>
      </UserInformation>
      <UserFieldWrapper>
        <Button
          type="button"
          look="link"
          palette="brand"
          size="md"
          onClick={resetPasswordHandle}
          fetching={state.resetPasswordFetching}
          disabled={
            !state.user.is_last_email_confirmed || projectStorage === UserStorageTypes.firebase
          }
          data-test-id="user-reset-password">
          {intl.messages['users.settings.general-info.reset-pass']}
          <ArrowClockwise />
        </Button>
      </UserFieldWrapper>
      <UserFieldWrapper>
        {banData?.date_from ? (
          <Button
            type="button"
            look="link"
            palette="brand"
            size="md"
            onClick={unbanHandle}
            fetching={state.unbanFetching}
            data-test-id="user-unban">
            {intl.messages['users.settings.general-info.unban-user']}
            <Trash />
          </Button>
        ) : (
          <Button
            type="button"
            look="link"
            palette="danger"
            size="md"
            onClick={onOpenBanUserModal}
            data-test-id="user-ban">
            {intl.messages['users.settings.general-info.ban-user']}
            <BlockCircle />
          </Button>
        )}
      </UserFieldWrapper>
      <UserLoginHistory />
    </SettingsBlock>
  );
};

export default GeneralInfo;
